import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { useAppDispatch } from "@/store";
import { deleteFile } from "@/store/filesSlice";
import { formatFileSize, formatRelativeTime } from "@/utils/format";
import type { UploadedFile } from "@/types";

function getFileIcon(mimeType: string) {
  if (mimeType.includes("pdf")) return <PictureAsPdfIcon sx={{ color: "#EF4444" }} />;
  if (mimeType.includes("zip")) return <FolderZipIcon sx={{ color: "#3B82F6" }} />;
  if (mimeType.startsWith("image/")) return <ImageIcon sx={{ color: "#8B5CF6" }} />;
  return <InsertDriveFileIcon sx={{ color: "#6B7280" }} />;
}

function getIconBg(mimeType: string, isDark: boolean): string {
  if (isDark) {
    if (mimeType.includes("pdf")) return "rgba(239, 68, 68, 0.18)";
    if (mimeType.includes("zip")) return "rgba(59, 130, 246, 0.18)";
    if (mimeType.startsWith("image/")) return "rgba(139, 92, 246, 0.2)";
    return "rgba(156, 163, 175, 0.15)";
  }
  if (mimeType.includes("pdf")) return "#FEF2F2";
  if (mimeType.includes("zip")) return "#EFF6FF";
  if (mimeType.startsWith("image/")) return "#F5F3FF";
  return "#F3F4F6";
}

interface FileTableProps {
  files: UploadedFile[];
  showActions?: boolean;
}

export function FileTable({ files, showActions = true }: FileTableProps) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  const handleDelete = async (id: string) => {
    await dispatch(deleteFile(id));
    notify(t("files.deleted"), "success");
  };

  if (files.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          {t("files.noFiles")}
        </Typography>
      </Box>
    );
  }

  const isDark = theme.palette.mode === "dark";

  const actionButtons = (file: UploadedFile) =>
    showActions ? (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        <Tooltip title={t("common.delete")}>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(file.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    ) : null;

  if (isNarrow) {
    return (
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {files.map((file) => (
          <Card key={file.id} variant="outlined" sx={{ overflow: "visible" }}>
            <CardContent
              sx={{
                py: 1.5,
                px: 2,
                "&:last-child": { pb: 1.5 },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: getIconBg(file.mimeType, isDark),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {getFileIcon(file.mimeType)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                    }}
                  >
                    {file.originalName}
                  </Typography>
                  <Stack
                    direction="row"
                    sx={{
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      · {formatRelativeTime(file.createdAt)}
                    </Typography>
                    <Chip
                      label={t("status.ready")}
                      size="small"
                      sx={(tt) => ({
                        height: 22,
                        fontWeight: 600,
                        fontSize: 11,
                        bgcolor: tt.palette.mode === "dark" ? "rgba(16, 185, 129, 0.2)" : "#ECFDF5",
                        color: tt.palette.mode === "dark" ? "#34D399" : "#059669",
                        border:
                          tt.palette.mode === "dark"
                            ? "1px solid rgba(52, 211, 153, 0.35)"
                            : "1px solid #A7F3D0",
                      })}
                    />
                  </Stack>
                </Box>
                {actionButtons(file)}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 520 }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                color: "text.secondary",
                width: "38%",
              }}
            >
              {t("files.name")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                color: "text.secondary",
                width: "12%",
              }}
            >
              {t("files.size")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                color: "text.secondary",
                width: "18%",
              }}
            >
              {t("files.uploaded")}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                color: "text.secondary",
                width: "14%",
              }}
            >
              {t("files.status")}
            </TableCell>
            {showActions && (
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: "uppercase",
                  color: "text.secondary",
                  width: "10%",
                }}
                align="right"
              >
                {t("files.actions")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id} hover>
              <TableCell sx={{ verticalAlign: "middle" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: getIconBg(file.mimeType, isDark),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {getFileIcon(file.mimeType)}
                  </Box>
                  <Typography
                    variant="body2"
                    title={file.originalName}
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.originalName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ verticalAlign: "middle" }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {formatFileSize(file.size)}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: "middle" }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {formatRelativeTime(file.createdAt)}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: "middle" }}>
                <Chip
                  label={t("status.ready")}
                  size="small"
                  sx={(tt) => ({
                    bgcolor: tt.palette.mode === "dark" ? "rgba(16, 185, 129, 0.2)" : "#ECFDF5",
                    color: tt.palette.mode === "dark" ? "#34D399" : "#10B981",
                    fontWeight: 600,
                    fontSize: 12,
                    border:
                      tt.palette.mode === "dark"
                        ? "1px solid rgba(52, 211, 153, 0.35)"
                        : "1px solid #A7F3D0",
                  })}
                />
              </TableCell>
              {showActions && (
                <TableCell align="right" sx={{ verticalAlign: "middle" }}>
                  <Tooltip title={t("common.delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
